/**
 * useBulkTransactionDraft Hook
 *
 * 거래 일괄 입력(A-7)의 행 상태를 관리한다.
 *
 * @why 화면 표현이 폭에 따라 테이블/카드로 갈리므로, 상태와 규칙을 표현에서 떼어
 * 이 훅 한 곳에 모은다. 표현 컴포넌트는 값을 받아 그리고 변경을 올려보내기만 한다.
 *
 * 자동 채움을 useEffect가 아니라 변경 핸들러에서 처리하는 이유:
 * useEffect(deps=[값])로 두면 데이터 로딩 순서나 초기 렌더 타이밍에 따라
 * 사용자가 고른 값을 덮어쓸 수 있다. "사용자가 실제로 바꾸는 순간"에만 실행되는
 * 명령형 처리가 결과를 예측 가능하게 만든다. (단건 폼에서 얻은 교훈과 동일)
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { PaymentMethod } from '@/domains/payment/types/PaymentMethod'
import type { BulkTransactionDraftRow } from '../types/BulkTransaction'
import { resolveSpenderForPaymentMethod } from '../utils/spenderResolver'

/** 한 번에 입력할 수 있는 최대 행 수 */
export const DEFAULT_MAX_ROWS = 50

/** 이 행 수를 넘으면 화면에서 "너무 많다" 안내를 띄운다 */
export const ROW_COUNT_WARNING_THRESHOLD = 30

export interface UseBulkTransactionDraftOptions {
  /** 결제수단 → 결제 계좌 소유자 계산에 사용 */
  paymentMethods: PaymentMethod[]
  /**
   * 작성 중인 내용을 보관할 localStorage 키. 주지 않으면 보관하지 않는다.
   *
   * @why 다건을 몰아서 입력하는 화면이라 실수로 나가거나 새로고침했을 때
   * 잃는 양이 단건 등록과 비교가 안 된다.
   *
   * 키를 호출측에서 받는 이유: 워크스페이스마다 카테고리·결제수단·멤버가 달라
   * 드래프트를 섞으면 참조가 전부 어긋난 행이 되살아난다. 워크스페이스별로
   * 키를 나눌 책임은 그 정보를 아는 호출측에 둔다.
   */
  storageKey?: string
  /**
   * 수입의 "받은 사람" 기본값 (현재 로그인 사용자)
   *
   * @why 수입은 보통 등록하는 본인이 받는다. 지출과 달리 결제수단으로 유추할 수 없어
   * 로그인 사용자를 기본값으로 둔다.
   */
  defaultSpenderId?: string
  maxRows?: number
}

export interface UseBulkTransactionDraftReturn {
  rows: BulkTransactionDraftRow[]
  addRow: () => void
  updateRow: (clientId: string, patch: Partial<BulkTransactionDraftRow>) => void
  removeRow: (clientId: string) => void
  canAddRow: boolean
  /**
   * 작성 중인 내용을 비우고 빈 행 하나로 되돌린다.
   *
   * @why 저장이 끝난 뒤 호출한다. 남겨 두면 다음에 화면을 열었을 때
   * 이미 저장된 내용이 되살아나 같은 거래를 두 번 저장하게 된다.
   */
  clearDraft: () => void
}

/** 오늘 날짜 (YYYY-MM-DD) */
function today(): string {
  return new Date().toISOString().split('T')[0] || ''
}

/**
 * 빈 행 생성
 *
 * @param seed 직전 행에서 이어받을 값 (없으면 완전히 빈 행)
 */
function createRow(
  seed?: Partial<BulkTransactionDraftRow>
): BulkTransactionDraftRow {
  return {
    // 배열 인덱스가 아닌 안정적인 식별자를 쓴다. 인덱스를 React key로 쓰면
    // 행 삭제 시 입력 중이던 칸의 포커스와 한글 조합 상태가 다른 행으로 옮겨간다.
    clientId: crypto.randomUUID(),
    type: 'expense',
    date: today(),
    amount: '',
    majorCategoryId: '',
    minorCategoryId: '',
    paymentMethodId: '',
    spenderId: '',
    memo: '',
    ...seed,
  }
}

/**
 * 사용자가 실제로 무언가 입력한 행인지 판정한다.
 *
 * @why 행을 추가하면 직전 행의 날짜·구분·결제수단이 자동으로 채워진다.
 * 그 값들은 "사용자가 입력했다"의 근거가 될 수 없으므로,
 * 자동으로 채워지지 않는 필드만 본다. (검증의 빈 행 판정과 같은 기준)
 */
function hasUserInput(row: BulkTransactionDraftRow): boolean {
  return (
    row.amount.trim() !== '' ||
    row.majorCategoryId !== '' ||
    row.memo.trim() !== ''
  )
}

/**
 * 보관된 내용을 읽어 되살린다.
 *
 * @why 저장소 값은 언제든 손상될 수 있다(다른 버전이 쓴 값, 수동 편집, 용량 초과 등).
 * 되살리기에 실패했다고 화면이 뜨지 않으면 안 되므로, 어떤 실패든 빈 화면으로 넘어간다.
 */
function restoreRows(storageKey: string | undefined): BulkTransactionDraftRow[] | null {
  if (!storageKey || typeof localStorage === 'undefined') return null

  try {
    const saved = localStorage.getItem(storageKey)
    if (!saved) return null

    const parsed: unknown = JSON.parse(saved)

    // 형태가 기대와 다르면(구버전 형식 등) 되살리지 않는다
    if (!Array.isArray(parsed) || parsed.length === 0) return null

    return parsed as BulkTransactionDraftRow[]
  } catch {
    return null
  }
}

export function useBulkTransactionDraft({
  paymentMethods,
  defaultSpenderId = '',
  maxRows = DEFAULT_MAX_ROWS,
  storageKey,
}: UseBulkTransactionDraftOptions): UseBulkTransactionDraftReturn {
  const [rows, setRows] = useState<BulkTransactionDraftRow[]>(
    () => restoreRows(storageKey) ?? [createRow()]
  )

  /**
   * 결제수단으로부터 "그 행에 들어가야 할 사람"을 결정한다.
   *
   * - 지출: 결제 계좌 소유자 = 결제수단의 소유자 (현금은 특정 불가라 빈칸)
   * - 수입: 결제수단과 무관한 "받은 사람" = 로그인 사용자
   */
  const resolveSpender = useCallback(
    (type: BulkTransactionDraftRow['type'], paymentMethodId: string): string => {
      if (type === 'income') return defaultSpenderId

      const paymentMethod = paymentMethods.find((pm) => pm.id === paymentMethodId)
      return resolveSpenderForPaymentMethod(paymentMethod)
    },
    [paymentMethods, defaultSpenderId]
  )

  /**
   * 행 추가
   *
   * 직전 행에서 날짜·구분·결제수단만 이어받는다. 금액/카테고리/메모는 건마다 달라
   * 이어받으면 오히려 지우는 수고가 생긴다.
   * 사람(spenderId)은 이어받지 않고 규칙으로 다시 계산한다 — 앞 행에서 수동 변경된
   * 값을 그대로 복사하면 결제수단과 어긋난 조합이 조용히 퍼진다.
   */
  const addRow = useCallback(() => {
    setRows((prev) => {
      if (prev.length >= maxRows) return prev

      const last = prev[prev.length - 1]
      const type = last?.type ?? 'expense'
      const paymentMethodId = last?.paymentMethodId ?? ''

      return [
        ...prev,
        createRow({
          type,
          date: last?.date ?? today(),
          paymentMethodId,
          spenderId: resolveSpender(type, paymentMethodId),
        }),
      ]
    })
  }, [maxRows, resolveSpender])

  /**
   * 행 수정
   *
   * 단순 대입이 아니라, 함께 정리되어야 하는 값을 같은 갱신에서 처리한다.
   * (타입 전환 시 결제수단·사람, 대분류 변경 시 소분류, 결제수단 변경 시 사람)
   */
  const updateRow = useCallback(
    (clientId: string, patch: Partial<BulkTransactionDraftRow>) => {
      setRows((prev) =>
        prev.map((row) => {
          if (row.clientId !== clientId) return row

          const next = { ...row, ...patch }

          // 구분 전환: 수입에는 결제수단 칸이 없으므로 남은 값을 정리하고
          // 받은 사람 기본값을 채운다. 화면에서 고칠 수 없는 값이 남지 않게 하기 위함.
          if (patch.type !== undefined && patch.type !== row.type) {
            next.paymentMethodId = patch.type === 'income' ? '' : next.paymentMethodId
            next.spenderId = resolveSpender(next.type, next.paymentMethodId)
          }

          // 결제수단 변경: 결제 계좌 소유자는 결제수단에 종속되므로 다시 계산한다.
          // (사용자가 이후 수동으로 다른 사람을 고를 수 있다)
          if (
            patch.paymentMethodId !== undefined &&
            patch.paymentMethodId !== row.paymentMethodId &&
            patch.spenderId === undefined
          ) {
            next.spenderId = resolveSpender(next.type, next.paymentMethodId)
          }

          // 대분류 변경: 이전 소분류는 새 대분류의 하위가 아니므로 비운다.
          // 같은 값을 다시 고른 경우는 변경이 아니므로 유지한다.
          if (
            patch.majorCategoryId !== undefined &&
            patch.majorCategoryId !== row.majorCategoryId &&
            patch.minorCategoryId === undefined
          ) {
            next.minorCategoryId = ''
          }

          return next
        })
      )
    },
    [resolveSpender]
  )

  /**
   * 행 삭제
   *
   * 행이 하나도 없으면 입력할 칸이 사라져 아무것도 못 하는 화면이 되므로
   * 마지막 행을 지우면 빈 행 하나로 되돌린다.
   */
  const removeRow = useCallback((clientId: string) => {
    setRows((prev) => {
      const remaining = prev.filter((row) => row.clientId !== clientId)
      return remaining.length > 0 ? remaining : [createRow()]
    })
  }, [])

  const clearDraft = useCallback(() => {
    setRows([createRow()])

    if (storageKey && typeof localStorage !== 'undefined') {
      try {
        localStorage.removeItem(storageKey)
      } catch {
        // 저장소를 못 써도 화면 동작에는 지장이 없으므로 조용히 넘어간다
      }
    }
  }, [storageKey])

  /**
   * 입력 내용을 저장소에 반영한다.
   *
   * @why 여기서만 useEffect를 쓰는 이유: 보관은 "행이 어떻게 바뀌었든 최종 상태를
   * 따라가기만 하면 되는" 부수효과라, 변경 경로마다 저장 코드를 심는 것보다
   * 상태 변화를 한 곳에서 따라가는 편이 빠뜨릴 여지가 없다.
   * (사용자가 고른 값을 덮어쓸 위험이 있는 자동 채움과는 성격이 다르다)
   *
   * 아무도 손대지 않은 상태에서 굳이 저장소를 더럽히지 않도록,
   * 실제 입력이 있는 행이 하나도 없으면 기록 대신 지운다.
   */
  useEffect(() => {
    if (!storageKey || typeof localStorage === 'undefined') return

    try {
      if (rows.some(hasUserInput)) {
        localStorage.setItem(storageKey, JSON.stringify(rows))
      } else {
        localStorage.removeItem(storageKey)
      }
    } catch {
      // 용량 초과·사생활 보호 모드 등으로 저장이 막혀도 입력은 계속할 수 있어야 한다
    }
  }, [rows, storageKey])

  const canAddRow = useMemo(() => rows.length < maxRows, [rows.length, maxRows])

  return { rows, addRow, updateRow, removeRow, canAddRow, clearDraft }
}
